<ng-container
  *ngTemplateOutlet="isEditMode ? editMode : readMode"
></ng-container>

<ng-template #editMode>
  <form class="variants-grid" [formGroup]="form()">
    <div class="service-header">
      {{ 'upgrade.pfa.wizard.section-0.form.serviceLabel' | transloco }}
    </div>
    <div class="service-content">
      <p>{{ form().controls.cartridge.value?.name }}</p>
    </div>
    <div class="variant-header">
      {{ 'upgrade.pfa.wizard.section-0.form.serviceVariantLabel' | transloco }}
    </div>
    <div class="variant-content">
      @if (upgradableVariantOptions.length) {
        <markant-select
          data-cy="upgrade-service-variant-candidate-select"
          formControlName="variant"
          [options]="upgradableVariantOptions"
          [value]="variant.value"
          [error]="
            variant.errors
              | formControlKeyError
                : 'upgrade.pfa.wizard.section-0.form.errors'
                : showErrors() || variant.touched || variant.dirty
              | transloco
          "
          [helper]="''"
          (valueChange)="onVariantSelect($event)"
        >
        </markant-select>
      }
    </div>
    <div class="billing-header">
      {{ 'upgrade.pfa.wizard.section-0.form.platformBillingLabel' | transloco }}
    </div>
    <div class="billing-content">
      <markant-select
        data-cy="upgrade-service-variant-platform-billing-select"
        formControlName="billingViaPlatform"
        [options]="billingViaPlatformOptions"
        [value]="billingViaPlatform.value"
      >
      </markant-select>
    </div>
    <div class="pricing-header">
      {{ 'upgrade.pfa.wizard.section-0.form.individualPriceLabel' | transloco }}
    </div>
    <div class="pricing-content">
      @if (individualPrice.value === NOT_APPLICABLE) {
        <markant-input
          data-cy="upgrade-service-variant-individual-price-na-input"
          type="text"
          formControlName="individualPrice"
          [value]="individualPrice.value"
          [error]="
            individualPrice.errors
              | formControlError
                : showErrors() ||
                    individualPrice.touched ||
                    individualPrice.dirty
          "
          [errorText]="
            individualPrice.errors
              | formControlKeyError
                : 'upgrade.pfa.wizard.section-0.form.errors'
                : showErrors() ||
                    individualPrice.touched ||
                    individualPrice.dirty
              | transloco
          "
          [attr.unit]="
            selectedVariantPrice()
              ? currencySymbols[selectedVariantPrice()!.currency]
              : null
          "
          [min]="0"
        ></markant-input>
      } @else {
        <markant-currency-input
          data-cy="upgrade-service-variant-individual-price-currency-input"
          currencyLocale="{{ activeLanguage$ | async }}"
          type="text"
          formControlName="individualPrice"
        >
          <markant-input
            data-cy="upgrade-service-variant-individual-price-currency-inner-input"
            type="text"
            [disabled]="individualPrice.disabled"
            [value]="individualPrice.value"
            [error]="
              individualPrice.errors
                | formControlError
                  : showErrors() ||
                      individualPrice.touched ||
                      individualPrice.dirty
            "
            [errorText]="
              individualPrice.errors
                | formControlKeyError
                  : 'upgrade.pfa.wizard.section-0.form.errors'
                  : showErrors() ||
                      individualPrice.touched ||
                      individualPrice.dirty
                | transloco
            "
            [attr.unit]="
              selectedVariantPrice()
                ? currencySymbols[selectedVariantPrice()!.currency]
                : null
            "
            [min]="0"
          ></markant-input>
        </markant-currency-input>
      }
    </div>
    <div class="org-admin-header">
      {{
        'upgrade.pfa.wizard.section-0.form.responsibleUserEmailLabel'
          | transloco
      }}
    </div>
    <div class="org-admin-content">
      <markant-input
        data-cy="upgrade-service-variant-orgAdminEmail-input"
        title="{{ booking()?.responsibleUserEmail ?? '' }}"
        formControlName="responsibleUserEmail"
        [name]="booking()?.responsibleUserEmail ?? ''"
        (wheel)="preventScroll($event)"
      ></markant-input>
    </div>
    <div class="booking-reference-header">
      {{
        'upgrade.pfa.wizard.section-0.form.bookingReferenceLabel' | transloco
      }}
    </div>
    <div class="booking-reference-content">
      <markant-input
        data-cy="upgrade-service-variant-bookingReference-input"
        title="{{ bookingReference.value }}"
        formControlName="bookingReference"
        (wheel)="preventScroll($event)"
      ></markant-input>
    </div>
  </form>
</ng-template>

<!-- READ ONLY MODE -->

<ng-template #readMode>
  <section class="readonly-grid variants-grid">
    <div class="service-header">
      {{ 'upgrade.pfa.wizard.section-0.form.serviceLabel' | transloco }}
    </div>
    <div class="readonly service-content">
      <p>{{ form().controls.cartridge.value!.name }}</p>
    </div>
    <div class="variant-header">
      {{ 'upgrade.pfa.wizard.section-0.form.serviceVariantLabel' | transloco }}
    </div>
    <div class="readonly variant-content">
      {{ selectedVariant()!.name }}
    </div>
    <div class="billing-header">
      {{ 'upgrade.pfa.wizard.section-0.form.platformBillingLabel' | transloco }}
    </div>
    <div class="readonly billing-content">
      {{
        billingViaPlatoformLabelRecord[
          billingViaPlatformControlOnReadonly.value
        ]
      }}
    </div>
    <div class="pricing-header">
      {{ 'upgrade.pfa.wizard.section-0.form.individualPriceLabel' | transloco }}
    </div>
    <div class="readonly pricing-content">
      @if (form().controls.individualPrice.value === NOT_APPLICABLE) {
        {{ form().controls.individualPrice.value }}
      } @else if (form().controls.individualPrice.value === null) {
        {{
          'upgrade.pfa.wizard.section-0.form.individualPriceNotSet' | transloco
        }}
      } @else {
        {{
          form().controls.individualPrice.value
            | currency: selectedVariantPrice()!.currency
        }}
      }
    </div>
    <div class="org-admin-header">
      {{
        'upgrade.pfa.wizard.section-0.form.responsibleUserEmailLabel'
          | transloco
      }}
    </div>
    <div class="readonly org-admin-content">
      <label class="label"
        >{{ booking()?.responsibleUserFirstname }}
        {{ booking()?.responsibleUserLastname }}</label
      >
      {{ form().controls.responsibleUserEmail.value }}
    </div>
    <div class="booking-reference-header">
      {{
        'upgrade.pfa.wizard.section-0.form.bookingReferenceLabel' | transloco
      }}
    </div>
    <div class="readonly booking-reference-content">
      {{ form().controls.bookingReference.value }}
    </div>
  </section>
</ng-template>
